import {
  Field,
  Image,
  ImageField,
  Link,
  LinkField,
  TextField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { EmblaOptionsType } from 'embla-carousel';

import { icon_chevron_left, icon_chevron_right } from '@gn-audio/icons';
import {
  Button,
  ComponentProps,
  JabraIcon,
  useFallbackTranslation,
  useJabraTheme,
  useSiteSettings,
  withTheme,
} from '@gn-audio/jss-components';

import { JabraSlide, JabraSlider } from 'common/components/JabraSlider/JabraSlider';
import { usePathMatch } from 'composables/shared/usePathMatch';

import tailwindBreakpoints from '../utils/tailwind/breakpoints.preval';

export type PaymentMethod = {
  logo: ImageField;
  link: LinkField;
  linkAriaLabel?: Field<string>;
  id: string;
};

export type PaymentMethodsBannerProps = {
  fields: {
    paymentMethods: PaymentMethod[];
    blacklistPaths: TextField;
  };
} & ComponentProps;

const PaymentMethodsBanner = ({ fields }: PaymentMethodsBannerProps) => {
  const { t } = useFallbackTranslation();
  const { isCurrentPathMatched } = usePathMatch();
  const { ecom } = useSiteSettings();
  const jabraTheme = useJabraTheme();

  if (!ecom?.ecomEnabled) {
    return null;
  }

  const paymentBlackListPathes = fields.blacklistPaths;
  const isRenderingDisabled: boolean = isCurrentPathMatched(
    paymentBlackListPathes?.value as string
  );
  const spotTheme = jabraTheme?.darkTheme ? 'dark' : 'light';

  if (isRenderingDisabled) return null;

  const paymentMethods = fields.paymentMethods;
  if (!paymentMethods?.length) return null;

  const paymentSlides = paymentMethods.map((method) => {
    const paymentLogo = method.logo;
    if (!paymentLogo?.value) {
      return null;
    }
    const PaymentSlideContent = () => (
      <Image
        field={paymentLogo}
        className="h-10 w-auto rounded-lg bg-white"
        alt={paymentLogo?.value?.alt || t('system.PaymentMethodLogo')}
        loading="lazy"
      />
    );

    const paymentLink = method.link;

    return (
      <JabraSlide key={method.id}>
        {!!paymentLink?.value?.href ? (
          <Link
            {...(method.linkAriaLabel?.value && { 'aria-label': method.linkAriaLabel.value })}
            href={paymentLink.value.href}
            field={paymentLink}
            className={'rounded-lg hover:shadow-md'}
          >
            <PaymentSlideContent />
          </Link>
        ) : (
          <PaymentSlideContent />
        )}
      </JabraSlide>
    );
  });

  const CustomButton = (
    direction: 'left' | 'right',
    isEnabled: boolean,
    clickHandler: () => void
  ) => (
    <Button
      size="s"
      variant="quaternary"
      className={direction === 'left' ? 'mr-3' : 'ml-3'}
      disabled={!isEnabled}
      title={direction === 'left' ? t('system.Previous') : t('system.Next')}
      icon={
        <JabraIcon
          className={classNames(`flex h-3 ${!isEnabled && 'opacity-50'}`)}
          Icon={direction === 'left' ? icon_chevron_left : icon_chevron_right}
        />
      }
      onClick={clickHandler}
    />
  );

  const itemsPerSlideMobile = 3;
  const itemsPerSlideDesktop = 9;
  const shouldItemsCenterForMobile = paymentSlides.length < itemsPerSlideMobile;
  const shouldItemsCenterForDesktop = paymentSlides.length < itemsPerSlideDesktop;
  const paymentSliderSettings: EmblaOptionsType = {
    slidesToScroll: itemsPerSlideMobile,
    containScroll: shouldItemsCenterForMobile ? false : 'trimSnaps',
    breakpoints: {
      [`(min-width: ${tailwindBreakpoints.lg})`]: {
        slidesToScroll: itemsPerSlideDesktop,
        containScroll: shouldItemsCenterForDesktop ? false : 'trimSnaps',
      },
    },
  };

  return (
    <div className={classNames(spotTheme, 'bg-theme container-fluid')}>
      <div className="container py-0">
        <h2 className="heading-6 text-center">{t('system.PaymentMethods')}</h2>
        <JabraSlider
          className="mb-2"
          mainClassName="flex w-full"
          slideClassName={`basis-1/3 lg:basis-[calc(100%/9)] h-10`}
          sliderSettings={paymentSliderSettings}
          customButton={CustomButton}
        >
          {paymentSlides}
        </JabraSlider>
      </div>
    </div>
  );
};

export default withTheme({
  defaultTheme: {
    backgroundColor: 'white',
    foregroundColor: 'black',
    darkTheme: false,
  },
})(withDatasourceCheck()<PaymentMethodsBannerProps>(PaymentMethodsBanner));
